import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import type { ConectarGoogleCalendarMutationMutation } from "@repo/graphql-types/graphql";
import { CpsSpinner } from "corpus";

import { GoogleCalendarConnectError } from "@/components/google-calendar/google-calendar-connect-error";
import { HeaderRoot, HeaderTitle } from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { fetchQuery, queryFnWithError } from "@/hooks/use-graphql";
import { trackEvent } from "@/lib/tracking";
import { logError } from "@/lib/utils/log-error";
import type { RootRouteContext } from "@/routes/-root/-root-context";
import { GoogleCalendarPageQuery } from "@/routes/settings/google-calendar";

import type { JSX } from "react";

const ConectarGoogleCalendarMutation = graphql(/* GraphQL */ `
  mutation ConectarGoogleCalendarMutation($authorizationCode: String!, $state: String!) {
    conectarGoogleCalendar(
      input: { authorizationCode: $authorizationCode, state: $state }
    ) {
      conectado
      googleEmail
      errors {
        ... on ValidationError {
          __typename
          message
        }
      }
    }
  }
`);

// Parâmetros que o Google anexa ao redirect_uri (GoogleCalendar:RedirectUri na API).
const googleCallbackSearch = z.object({
  code: z.string().optional(),
  state: z.string().optional(),
  error: z.string().optional(),
});

type GoogleCallbackSearch = z.infer<typeof googleCallbackSearch>;

/**
 * Troca o code do Google pela conexão do Google Agenda e devolve `true` quando conectou.
 * Roda no loader (uma vez por navegação), porque o code só vale uma troca.
 */
// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const exchangeGoogleAuthorization = async (
  context: RootRouteContext,
  { code, state, error }: GoogleCallbackSearch,
): Promise<boolean> => {
  if (!code || !state || error) {
    return false;
  }

  try {
    const result: ConectarGoogleCalendarMutationMutation = await queryFnWithError(
      ConectarGoogleCalendarMutation,
      context.authClient,
      { authorizationCode: code, state },
    );

    if (result.conectarGoogleCalendar.conectado !== true) {
      throw new Error("conectarGoogleCalendar não confirmou a conexão");
    }
  } catch (exchangeError) {
    logError(exchangeError);
    return false;
  }

  trackEvent("Google Agenda Conectado", { codUsuario: context.user.codUsuario });

  // Atualiza o status em cache para a tela de destino já abrir como conectada.
  await fetchQuery(context, GoogleCalendarPageQuery).catch(logError);

  return true;
};

const GoogleCalendarConnectingPage = (): JSX.Element => {
  return (
    <>
      <HeaderRoot>
        <HeaderTitle title="Google Agenda" />
      </HeaderRoot>
      <Page className="p-6">
        <div className="flex flex-col items-center justify-center h-full gap-6 px-2 text-center">
          <CpsSpinner />
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-semibold text-neutral-700">
              Conectando sua conta…
            </h2>
            <p className="text-sm text-neutral-500">
              Estamos trazendo seus próximos compromissos do Google Agenda. Isso leva só
              alguns segundos.
            </p>
          </div>
        </div>
      </Page>
    </>
  );
};

// Só renderiza quando a troca falha: no sucesso o loader redireciona.
const GoogleCalendarCallbackPage = (): JSX.Element => {
  const router = useRouter();

  // No app, o callback tomou o lugar da tela do Google Agenda, então voltar sai do fluxo. Na
  // web, o callback abre como página nova, sem entrada do router antes dele.
  const dismiss = (): void => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }

    void router.navigate({ to: "/settings", replace: true });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderTitle title="Google Agenda" />
      </HeaderRoot>
      <Page className="p-6">
        <GoogleCalendarConnectError
          onRetry={() =>
            void router.navigate({ to: "/settings/google-calendar", replace: true })
          }
          onDismiss={dismiss}
        />
      </Page>
    </>
  );
};

export const Route = createFileRoute("/google/callback")({
  validateSearch: googleCallbackSearch,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-integracao-google-agenda"]) {
      redirect({ to: "/settings", throw: true });
    }
  },
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const connected = await exchangeGoogleAuthorization(context, deps);

    if (connected) {
      redirect({ to: "/settings/google-calendar", replace: true, throw: true });
    }
  },
  pendingMs: 0,
  pendingComponent: GoogleCalendarConnectingPage,
  component: GoogleCalendarCallbackPage,
});
